import { AsyncPipe } from "@angular/common";
import { Component, ElementRef, OnDestroy, Renderer2, ViewEncapsulation } from "@angular/core";
import { AngularFireAnalytics } from "@angular/fire/compat/analytics";
import { doc, Firestore, getDoc } from "@angular/fire/firestore";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { Data, Router } from "@angular/router";
import { Share } from "@capacitor/share";
import { AuthService } from "@core/auth.service";
import { AuthSyndicateService } from "@core/AuthServices/authSyndicate/auth-syndicate.service";
import { MySnackBarService } from "@core/my-snack-bar.service";
import { IonicModule, ModalController } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { QRCodeComponent, QRCodeElementType } from "angularx-qrcode";
import { from, of, Subscription, throwError } from "rxjs";
import { catchError, map, switchMap, take } from "rxjs/operators";
import { CommonVariable } from "src/app/app.constants";
import { AccountRepositoryService } from "src/app/pages/account/account-repository.service";
import { CommonService } from "src/app/services/common/common.service";
import { FirestoreDataService } from "src/app/services/firestore-data.service";
import { GTMSagmentService } from "src/app/services/gtm-sagment.service";
import { MarketingCampaignsService } from "src/app/services/marketing-campaigns.service";
import { TrackedEventsService } from "src/app/services/tracked-events.service";
import { SafeHtmlPipe } from "../../pipe/safehtmlpipe";
import { MixpanelService } from "../../services/mixpanel.service";
import { HeaderComponent } from "../header/header.component";
import { ReferralModalComponent } from "../referral-modal/referral-modal.component";

@Component({
  selector: "app-refer-friend",
  templateUrl: "./refer-friend.component.html",
  styleUrls: ["./refer-friend.component.scss"],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    IonicModule,
    HeaderComponent,
    QRCodeComponent,
    AsyncPipe,
    SafeHtmlPipe,
    TranslatePipe,
  ],
})
export class ReferFriendComponent implements OnDestroy {
  selectedLanguage: string = "en";
  user$ = this.authService.userDetails$;
  mgm$ = this.marketingCampaignsService.getMGMDetails();
  shareUrl: unknown;
  userDetails: Data;
  elementType: QRCodeElementType = "canvas" as QRCodeElementType;
  translatedToastMsg: any;
  mgmLinkshareCount: number;
  mgmPageViewCount: number;
  scrollDepthValue: string;
  pageViewIncremented = false;
  mgmLinkShareIncremented = false;
  public preferredCurrency: string;
  public currencySymbol: string = CommonVariable.defaultCurrencySymbol;
  public intHeadTitle: string;
  public intHeadDescription: string;
  public intReferralMessage: string;
  intData: Data;
  MGMDetailsSubscription: Subscription;
  public referral_code: string;
  public syndicate_promotion = "";
  public userId: string;
  constructor(
    private modalController: ModalController,
    private accountService: AccountRepositoryService,
    private analytics: AngularFireAnalytics,
    private bottomSheet: MatBottomSheet,
    private authService: AuthService,
    private mixpanelService: MixpanelService,
    public renderer: Renderer2,
    public translate: TranslateService,
    private trackedEventsService: TrackedEventsService,
    private GTMSegment: GTMSagmentService,
    private snackService: MySnackBarService,
    private router: Router,
    private elementRef: ElementRef,
    private firestore: Firestore,
    private AuthSyndicateService: AuthSyndicateService,
    private marketingCampaignsService: MarketingCampaignsService,
    private firestoreDataService: FirestoreDataService,
    private commonService: CommonService
  ) {
    this.preferredCurrency = CommonVariable.preferredCurrency;
    this.currencySymbol = CommonVariable.defaultCurrencySymbol;
    this.GTMSegment.GTMPopupInitialization("ReferFriengPopup");
    this.authService?.selectedlanguage$?.subscribe(
      (l) => {
        this.selectedLanguage = l ? l : "en";
        translate.use(l ? l : "en");
        this.getMGMDetails();
      },
      (err) => {
        return err;
      }
    );
    this.authService.userDetails$.subscribe(
      (user) => {
        this.preferredCurrency = user?.preferred_currency ?? CommonVariable.preferredCurrency;
        this.authService.getCurrencyList$.subscribe((data: Data) => {
          const CurrencyDetails = data?.details?.find((x) => x?.currency_code === this.preferredCurrency);
          this.currencySymbol = CurrencyDetails?.currency_symbol ?? CommonVariable.defaultCurrencySymbol;
        });
      },
      (err) => {
        return err;
      }
    );

    this.getMGMDetails();

    this.authService?.getConstantsMsg$?.subscribe((msg: Data) => {
      this.translatedToastMsg = msg?.static_messages_pwa;
    });
    this.analytics.logEvent("refer_friend", {
      userId: this.userId,
    });

    // Call promotion action seen
    this.callPromoCF("seen");
  }

  getMGMDetails(): void {
    this.MGMDetailsSubscription = this.marketingCampaignsService?.getMGMDetails().subscribe((data: Data) => {
      this.intData = data;

      const getDV = (field: string) => {
        const lang = this.selectedLanguage || "en";
        return this.authService.getDVFromString(data?.[field]?.[lang]).then((dvString: string) => dvString || "");
      };

      Promise.all([getDV("int_head_title"), getDV("int_head_description"), getDV("int_referral_message")])
        .then(([title, description, referralMessage]) => {
          this.intHeadTitle = title;
          this.intHeadDescription = description;
          this.intReferralMessage = referralMessage;
        })
        .catch((err) => {
          return err;
        });
    });
  }

  ngOnDestroy(): void {
    this.MGMDetailsSubscription?.unsubscribe();
  }

  async ngOnInit() {
    await this.authService.getUid("ReferFriendComponent").then((uid) => {
      this.userId = uid;
    })
    let startTime = new Date();
    // Auth Service Get User Details
    this.authService?.userDetails$.subscribe(
      (data: Data) => {
        const zero = 0;
        // Store mgm page view count and share count
        this.mgmPageViewCount = data?.mgm_page_view_count || zero;
        this.mgmLinkshareCount = data?.mgm_link_share_count || zero;
        this.referral_code = data?.referral_code;
        // Increment mgmPageViewCount each time the user views the page
        if (!this.pageViewIncremented) {
          this.mgmPageViewCount++;
          this.pageViewIncremented = true;

          // Store mgmPageViewCount in Firestore
          const path_id = "yzF1Ss5eiMqnh91zQMtv";
          const doc_data = { mgm_page_view_count: this.mgmPageViewCount };
          const method = "update";

          this.authService.getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data,
            method,
          }).subscribe({
            error: (err) => {
              console.error('Error updating mgmPageViewCount:', err);
            }
          });
        }
      },
      (error) => {
        console.error("Error fetching user details:", error);
      }
    );

    setTimeout(() => {
      const scrollDepth = this.calculateSectionScrollDepth()?.toFixed(2);
      const scrollDepthValue = scrollDepth;

      localStorage.setItem('scrollDepthValue', scrollDepthValue);
      // Ensure scroll depth value is valid
      const storedScrollDepthValue = localStorage.getItem('scrollDepthValue');
      const scrollDepthPercentage = (!storedScrollDepthValue || isNaN(parseFloat(storedScrollDepthValue)))
        ? "100.00%"
        : storedScrollDepthValue + "%";

      let endTime = new Date();
      let dwellTime = endTime.getTime() - startTime.getTime();
      let formattedDwellTime = this.AuthSyndicateService.convertMillisecondsToTime(dwellTime);

      const body = {
        Scroll_Depth: scrollDepthPercentage,
        Dwell_Time: formattedDwellTime,
        Count_Of_Previous_Views: this.mgmPageViewCount,
        Count_Of_Previous_Shares: this.mgmLinkshareCount,
        MGM_Code: this.referral_code,
      };
      this.GTMSegment?.GTMEventPopupInitialization("MGM Page Viewed", body);
    }, 2000);
    // Generate referral short URL
    this.generateReferralShortUrl();
  }
  openReferralModal() {
    this.bottomSheet.open(ReferralModalComponent, {
      panelClass: ["referral-friend-bottom-sheet", "bottom-sheet-transparent"],
      data: this.mgm$,
    });
  }

  backToTabs() {
    if (window.location.href.search("refer-friends") > -1) {
      this.router.navigateByUrl("mainTabs/tabs/home");
      return;
    }
    this.modalController.dismiss().catch((err) => {
      return err;
    });
  }


  invite(mgm: any) {
    this.commonService.getUid("InviteMGM").pipe(take(1)).subscribe(async (userId: string | null) => {
      if (!userId) return;

      this.callPromoCF("click");

      const notificationDocPath = `users/${userId}/in_app_notification/status`;
      from(getDoc(doc(this.firestore, notificationDocPath)))
        .pipe(
          switchMap((docSnapshot) => {
            const inAppNotificationData = docSnapshot.data();
            const doc_data = inAppNotificationData?.out_of_points_promotion
              ? {
                out_of_points_promotion: { updatedOn: new Date() },
                referred_friend: { status: true, updatedOn: new Date() },
              }
              : {
                referred_friend: { status: true, updatedOn: new Date() },
              };

            return this.authService.getV2CloudFunction("updatefirestoredoc", {
              path_id: "KdpTQsnyjKvgqvjItVvT",
              doc_data,
              method: "update",
            }).pipe(catchError((error) => of(error)));
          })
        )
        .subscribe();
      // Update promo_details in Firestore
      const path_id = "1cblQl68QltRugb1YDAY";
      const method = "read";

      this.authService?.getV2CloudFunction("updatefirestoredoc", {
        path_id,
        method,
      }).pipe(
        map((snapshot: any) => snapshot?.data)
      ).subscribe({
        next: (docSnap: any) => {
          if (docSnap) {
            const promoDetails = docSnap?.promo_details || [];
            const updatedPromoDetails = promoDetails.map(promo => promo.promo_name === "refer_friend" ? { ...promo, status: true } : promo);
            this.authService.getV2CloudFunction("updatefirestoredoc", {
              path_id: "1cblQl68QltRugb1YDAY",
              doc_data: { promo_details: updatedPromoDetails },
              method: "update",
            }).pipe(catchError((error) => of(error))).subscribe();
          }
        },
        error: (error: any) => {
          console.error("Error fetching promo details:", error);
        }
      });
      if (!this.mgmLinkShareIncremented) {
        this.mgmLinkshareCount++;
        this.mgmLinkShareIncremented = true;

        this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id: "yzF1Ss5eiMqnh91zQMtv",
          doc_data: { mgm_link_share_count: this.mgmLinkshareCount },
          method: "update",
        }).pipe(catchError((error) => of(error))).subscribe();
      }

      this.GTMSegment?.GTMEventPopupInitialization("MGM Link Shared", {
        MGM_Link: this.shareUrl || null,
      });

      mgm.referral_link = this.shareUrl;
      this.inviteViaSocialsharing(mgm);

      let isUpdatedCelebration = false;
      this.trackedEventsService?.listOfEvents$?.pipe().subscribe((events) => {
        if (!Array.isArray(events)) return;

        if (events.some((event) => event.id === "web_share_API")) {
          this.trackedEventsService.updateTrackedUserEvent("web_share_API");
        }

        events.forEach((event: any) => {
          if (event.id === "web_share_API" && !isUpdatedCelebration) {
            this.trackedEventsService.updateCelebration(event);
            isUpdatedCelebration = true;
          }
        });
      });

      setTimeout(() => {
        this.trackedEventsService.openAnimation();
      }, 2000);
    });
  }
  async inviteViaSocialsharing(mgm: any) {
    try {
      await Share.share({
        title: mgm.more_info_title,
        text: this.intReferralMessage,
        url: mgm.referral_link,
        dialogTitle: mgm.head_title,
      })
        .then(() => {
          this.callPromoCF("conversion");
        })
        .catch((err) => {
          // return err;
          // this.snackService.openSnackBar("something went wrong!", "", "Error", "Error");
          /* const msg = this.translatedToastMsg?.["1015"]?.[this.selectedLanguage];
          this.authService.showToast(msg ? msg : "Code Error", "Error", ""); */
          this.authService.getConstantsMsg("1015").then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "something went wrong!",
                line_2: "",
              },
              style: {
                icon: "tick circle error.svg",
                type: "Error",
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          })
            .catch((error) => {
              return error;
            });
        });
      this.friendSocialButtonsMixpanel("invite");
    } catch (Error) {
      // this.snackService.openSnackBar("something went wrong!", "", "Error", "Error");
      /* const msg = this.translatedToastMsg?.["1015"]?.[this.selectedLanguage];
      this.authService.showToast(msg ? msg : "Code Error", "Error", ""); */
      this.authService.getConstantsMsg("1015").then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: "something went wrong!",
            line_2: "",
          },
          style: {
            icon: "tick circle error.svg",
            type: "Error",
          },
        };
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      })
        .catch((error) => {
          return error;
        });
    }
  }
  public friendSocialButtonsMixpanel(txtRefer: any) {
    this.mixpanelService.init(this.userId);

    this.mixpanelService.track("MGM Link Shared", {
      current_url: window.location.href,
      share_method: txtRefer,
    });
  }

  saveAsImage(parent: any) {
    let parentElement = null;
    if (this.elementType === "canvas") {
      parentElement = parent.qrcElement.nativeElement.querySelector("canvas").toDataURL("image/png");
      this.accountService
        .updateQRCode(parentElement)
        .then((res: any) => {
          //this.snackService.openSnackBar("QR code updated", "", "Success", "Success");
          /* const msg = this.translatedToastMsg?.["1069"]?.[this.selectedLanguage];
          this.authService.showToast(msg ? msg : "Code Error", "Success", ""); */
          this.authService.getConstantsMsg("1069").then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "QR code updated",
                line_2: "",
              },
              style: {
                icon: "tick circle success.png",
                type: "Success",
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          });
        })
        .catch((err) => {
          throw new Error(`Error updating QR code: ${err}`)
        });
    }
  }
  // This method is used to generate referral short URL
  public generateReferralShortUrl() {
    // This method is used to retrieve user details for the database.
    this.authService.userDetails$.pipe(
      catchError((error) => {
        return throwError(error);
      })
    ).subscribe((data: Data) => {
      // variable containing referral Code
      this.referral_code = data?.referral_code;
    });
    // This method is used to generate  referral short URL
    this.accountService.syndicatePromotion$.pipe(take(1)).subscribe(
      (res) => {
        if (res) {
          this.syndicate_promotion = "refer_friend_promotion";
        }
      }
    )
    this.accountService.getShortReferralUrl(this.referral_code, this.syndicate_promotion).pipe(
      catchError((error) => {
        return throwError(error);

      })
    )
      .subscribe((res: string) => {
        try {
          this.shareUrl = res;
          this.accountService.setShortUrl(res);
        } catch (error) {
          throwError(error);
        }
      });

  }
  calculateSectionScrollDepth() {
    const sectionElement = this.elementRef?.nativeElement?.querySelector('#referFriendScroll');
    if (sectionElement) {
      const sectionHeight = sectionElement?.offsetHeight;
      const sectionTop = sectionElement?.getBoundingClientRect()?.top;
      const windowHeight = window?.innerHeight;

      // Calculate scroll depth relative to the section
      if (sectionTop >= 0 && sectionTop <= windowHeight) {
        const visibleHeight = Math.min(sectionHeight, windowHeight - sectionTop);
        return (visibleHeight / sectionHeight) * 100;
      } else {
        return 0;
      }
    }
  }

  // This method is used to call the promotion conversion API
  callPromoCF(action: string) {
    const promoName = localStorage.getItem("engagement_promo") || "";
    if (promoName != "refer_a_friend") return;
    this.authService.displayPromo(action, promoName);
  }
}
